import { z } from 'zod';
import { countryCodeSchema } from './countries';

/** When a script is set. Mirrors `time_period_is_valid` in SQL. */
export const TIME_PERIODS = [
  'present_day',
  'living_memory',
  'previous_century',
  'ancient_history',
  'near_future',
  'far_future',
] as const;
export const timePeriodSchema = z.enum(TIME_PERIODS);
export type TimePeriod = z.infer<typeof timePeriodSchema>;

/** What a script can be for. Mirrors `script_production_type_is_valid` in SQL. */
export const SCRIPT_PRODUCTION_TYPES = [
  'feature_film',
  'tv_episode',
  'short_film',
  'podcast',
  'other',
] as const;
export const scriptProductionTypeSchema = z.enum(SCRIPT_PRODUCTION_TYPES);
export type ScriptProductionType = z.infer<typeof scriptProductionTypeSchema>;

/** At most this many cities on a script project; an extraction offers at most `MAX_EXTRACTED_CITIES`. */
export const MAX_SCRIPT_CITIES = 10;
export const MAX_EXTRACTED_CITIES = 4;
export const MAX_SCRIPT_AUTHORS = 20;

/** A city a script is set in, with the country it is in. */
export const scriptCitySchema = z.strictObject({
  name: z.string().trim().min(1).max(100),
  country: countryCodeSchema,
});
export type ScriptCity = z.infer<typeof scriptCitySchema>;

/**
 * A row of `public.script_projects`: the intake details of a script clearance. Everything is empty while the form
 * is being filled in; an order cannot be submitted without a title and a production type. For a TV episode the
 * title is the show's.
 */
export const scriptProjectSchema = z.object({
  project_id: z.uuid(),
  production_type: scriptProductionTypeSchema.nullable(),
  title: z.string().min(1).max(200).nullable(),
  episode_title: z.string().min(1).max(200).nullable(),
  episode_number: z.string().min(1).max(50).nullable(),
  producer: z.string().min(1).max(200).nullable(),
  authors: z.array(z.string().min(1).max(200)),
  time_periods: z.array(timePeriodSchema),
  countries: z.array(countryCodeSchema),
  no_specific_country: z.boolean(),
  cities: z.array(scriptCitySchema),
  based_on_real: z.boolean().nullable(),
  based_on_real_note: z.string().min(1).max(2000).nullable(),
  notes: z.string().min(1).max(4000).nullable(),
});
export type ScriptProject = z.infer<typeof scriptProjectSchema>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => v || null);

/** What `update_script_project` takes: every field optional while drafting, trimmed, empty meaning none. */
export const scriptProjectInputSchema = z.strictObject({
  productionType: scriptProductionTypeSchema.nullable(),
  title: optionalText(200),
  episodeTitle: optionalText(200),
  episodeNumber: optionalText(50),
  producer: optionalText(200),
  authors: z
    .array(z.string().trim().max(200))
    .max(MAX_SCRIPT_AUTHORS)
    .transform((list) => list.filter((author) => author !== '')),
  timePeriods: z.array(timePeriodSchema).max(TIME_PERIODS.length),
  countries: z.array(countryCodeSchema),
  noSpecificCountry: z.boolean(),
  cities: z.array(scriptCitySchema).max(MAX_SCRIPT_CITIES),
  basedOnReal: z.boolean().nullable(),
  basedOnRealNote: optionalText(2000),
  notes: optionalText(4000),
});
export type ScriptProjectInput = z.infer<typeof scriptProjectInputSchema>;
